"use client";

import { useEffect, useRef, useState } from "react";

type TurnstileProps = {
  siteKey: string;
  size?: "normal" | "flexible" | "compact";
  onVerify: (token: string) => void;
  onExpire?: () => void;
  onError?: () => void;
  className?: string;
};

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          "expired-callback"?: () => void;
          "error-callback"?: () => void;
          "timeout-callback"?: () => void;
          theme?: "light" | "dark" | "auto";
          size?: "normal" | "flexible" | "compact";
        },
      ) => string;
      remove: (widgetId: string) => void;
    };
  }
}

const SCRIPT_ID = "turnstile-script";

function shouldBypassTurnstileForLocalDev() {
  if (typeof window === "undefined") return false;
  if (process.env.NODE_ENV === "production") return false;
  return ["localhost", "127.0.0.1"].includes(window.location.hostname);
}

export function Turnstile({ siteKey, onVerify, onExpire, onError, className, size }: TurnstileProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const callbacks = useRef({ onVerify, onExpire, onError });
  const [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    callbacks.current = { onVerify, onExpire, onError };
  }, [onVerify, onExpire, onError]);

  useEffect(() => {
    if (!siteKey || !containerRef.current) {
      return;
    }

    if (shouldBypassTurnstileForLocalDev()) {
      containerRef.current.dataset.testid = "turnstile-dev-bypass";
      containerRef.current.setAttribute("aria-label", "Local development Turnstile bypass");
      containerRef.current.innerHTML =
        '<div class="inline-flex rounded-xl border border-emerald/25 bg-emerald/10 px-3 py-2 text-xs text-emerald">Turnstile bypass enabled for local development</div>';
      callbacks.current.onVerify("dev-bypass");
      return;
    }

    let active = true;
    const handleError = () => {
      if (!active) return;
      setFailed(true);
      callbacks.current.onError?.();
    };
    const renderWidget = () => {
      if (!active || !window.turnstile || !containerRef.current || widgetIdRef.current) {
        return;
      }
      try {
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        callback: (token) => { if (active) { setFailed(false); callbacks.current.onVerify(token); } },
        "expired-callback": () => { if (active) callbacks.current.onExpire?.(); },
        "error-callback": handleError,
        "timeout-callback": handleError,
        theme: "auto",
        ...(size ? { size } : {}),
        });
      } catch {
        handleError();
      }
    };
    let script = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (script?.dataset.failed === "true") {
      script.remove();
      script = null;
    }
    if (script) {
      if (script.dataset.loaded === "true") {
        renderWidget();
      } else {
        script.addEventListener("load", renderWidget, { once: true });
      }
    } else {
      script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      const newScript = script;
      script.onload = () => { newScript.dataset.loaded = "true"; };
      script.addEventListener("load", renderWidget, { once: true });
      document.body.appendChild(script);
    }
    const scriptError = () => {
      if (script) script.dataset.failed = "true";
      handleError();
    };
    script.addEventListener("error", scriptError);

    return () => {
      active = false;
      script?.removeEventListener("load", renderWidget);
      script?.removeEventListener("error", scriptError);
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, size, retry]);

  return <div className={className}>
    <div ref={containerRef} role="group" aria-label="Human verification" />
    {failed ? <p role="alert" className="mt-2 text-sm text-red-700">Human verification could not finish. <button type="button" className="underline underline-offset-4" onClick={() => { setFailed(false); setRetry((value) => value + 1); }}>Try verification again</button></p> : null}
  </div>;
}
